import { FC, KeyboardEvent, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  InputBase,
  ListItemText,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { BorderedSelect } from '@components';
import { useTrans } from '@libs/i18n';
import { PlusIcon } from '@phosphor-icons/react';

import { asList } from '../../utils/fieldValue';
import { editBoxSx } from '../../utils/tokens';

import type { FieldInputProps } from './fieldInput.types';

/**
 * Renderer for list fields (string_list / number_list / multi_select). Read
 * mode shows neutral white pills; edit mode shows blue removable chips. A
 * `multi_select` field with predefined `options` edits via a checkbox dropdown
 * of those options; free-form lists edit via an inline dashed "Add" input.
 */
export const ListChipsField: FC<FieldInputProps> = observer(
  ({ field, value, mode, dirty, disabled, onChange }) => {
    const { t } = useTrans();
    const theme = useTheme();
    const [draft, setDraft] = useState('');
    const items = asList(value);
    const empty = items.length === 0;
    const isNumberList = field.type === 'number_list';
    const options = field.options ?? [];
    // Only `select`/`multi_select` carry options (catalog rule), and `select`
    // renders via ScalarField — so any list field here that has options is a
    // multi-select. Keying off options too makes the options dropdown show even
    // when the field's `type` doesn't round-trip as `multi_select`.
    const isMultiSelect = field.type === 'multi_select' || options.length > 0;

    const commit = (next: (string | number)[]) => onChange?.(next);

    const addDraft = () => {
      const trimmed = draft.trim();
      if (!trimmed) return;
      const parsed = isNumberList ? Number(trimmed) : trimmed;
      if (isNumberList && !Number.isFinite(parsed as number)) return;
      commit([...items, parsed]);
      setDraft('');
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        addDraft();
      }
    };

    // ── Read mode: neutral white pills ────────────────────────────────────
    if (mode === 'read') {
      if (empty) {
        return (
          <Typography
            variant="textSM_regular"
            color="text.disabled"
            sx={{ fontStyle: 'italic' }}
          >
            {t('app.orgProfile.field.noItems')}
          </Typography>
        );
      }
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {items.map((item, i) => (
            <Chip
              key={`${String(item)}-${i}`}
              label={String(item)}
              size="small"
              sx={{
                height: 26,
                typography: 'textSM_semiBold',
                color: 'text.primary',
                backgroundColor: 'background.paper',
                border: 1,
                borderColor: 'grey.300',
              }}
            />
          ))}
        </Box>
      );
    }

    // ── Edit mode (multi_select): checkbox dropdown of the field options ──
    if (isMultiSelect) {
      const selected = items.map(String);
      return (
        <BorderedSelect
          variant="field"
          multiple
          fullWidth
          displayEmpty
          dirty={dirty}
          disabled={disabled}
          value={selected}
          onChange={(e) => {
            const next = e.target.value as string[] | string;
            commit(typeof next === 'string' ? next.split(',') : next);
          }}
          renderValue={(value) => {
            const picked = value as string[];
            return picked.length === 0 ? (
              <Typography
                variant="textSM_regular"
                color="text.disabled"
                sx={{ fontStyle: 'italic' }}
              >
                {t('app.orgProfile.field.add')}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {picked.map((item) => (
                  <Chip
                    key={item}
                    label={item}
                    size="small"
                    sx={{
                      height: 26,
                      typography: 'textSM_semiBold',
                      color: 'blue.700',
                      backgroundColor: 'blue.100',
                      border: 1,
                      borderColor: 'blue.200',
                    }}
                  />
                ))}
              </Box>
            );
          }}
        >
          {options.map((o) => (
            <MenuItem key={o} value={o} sx={{ typography: 'textSM_regular' }}>
              <Checkbox size="small" checked={selected.includes(o)} />
              <ListItemText
                primary={o}
                primaryTypographyProps={{ variant: 'textSM_regular' }}
              />
            </MenuItem>
          ))}
        </BorderedSelect>
      );
    }

    // ── Edit mode: blue removable chips + dashed Add ──────────────────────
    return (
      <Box
        sx={{
          ...editBoxSx(dirty),
          px: 1.25,
          py: 1,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          alignItems: 'center',
          minHeight: 44,
        }}
      >
        {items.map((item, i) => (
          <Chip
            key={`${String(item)}-${i}`}
            label={String(item)}
            size="small"
            onDelete={
              disabled ? undefined : () => commit(items.filter((_, idx) => idx !== i))
            }
            sx={{
              height: 26,
              typography: 'textSM_semiBold',
              color: 'blue.700',
              backgroundColor: 'blue.100',
              border: 1,
              borderColor: 'blue.200',
              '& .MuiChip-deleteIcon': { color: 'blue.700', fontSize: 16 },
            }}
          />
        ))}

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            borderRadius: 999,
            border: '1px dashed',
            borderColor: 'grey.400',
            px: 1,
            py: '2px',
          }}
        >
          <PlusIcon size={11} color={theme.palette.text.tertiary} />
          <InputBase
            value={draft}
            disabled={disabled}
            placeholder={t('app.orgProfile.field.add')}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={addDraft}
            sx={{ typography: 'textXS_regular', width: 84 }}
            inputProps={isNumberList ? { inputMode: 'numeric' } : undefined}
          />
        </Box>
      </Box>
    );
  },
);
